import React, { useRef } from 'react';
import { MotionValue, useDragControls } from 'framer-motion';

const DEFAULT_DRAG_THRESHOLD = 6; // px

function withHandleDisabled(el: HTMLElement, fn: () => void) {
  const saved: Array<[HTMLElement, string]> = [];
  const off = (node: HTMLElement) => {
    saved.push([node, node.style.pointerEvents]);
    node.style.pointerEvents = 'none';
    for (const c of Array.from(node.children)) off(c as HTMLElement);
  };
  off(el);
  try {
    fn();
  } finally {
    for (const [node, prev] of saved) node.style.pointerEvents = prev || '';
  }
}

export type BottomSheetHandleProps = {
  controls: ReturnType<typeof useDragControls>;
  y: MotionValue<number>;
  height?: number;
  dragThreshold?: number;
  offsetY?: number;
};

export const BottomSheetDragHandle: React.FC<BottomSheetHandleProps> = ({ controls, height = 150, dragThreshold = DEFAULT_DRAG_THRESHOLD, offsetY }) => {
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const draggedRef = useRef(false);
  const lastForwardAtRef = useRef(0);

  function forwardClickToUnderlay(clientX: number, clientY: number) {
    const el = dragHandleRef.current;
    if (!el) return;
    withHandleDisabled(el, () => {
      const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      if (target) {
        target.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            clientX,
            clientY,
            view: window,
          })
        );
      }
    });
  }

  function onPointerDown(e: React.PointerEvent) {
    e.preventDefault();
    startRef.current = { x: e.clientX, y: e.clientY };
    draggedRef.current = false;
    controls.start(e);
  }

  function onPointerMove(e: React.PointerEvent) {
    const s = startRef.current;
    if (!s) return;
    const dx = e.clientX - s.x,
      dy = e.clientY - s.y;
    if (!draggedRef.current && dx * dx + dy * dy > dragThreshold * dragThreshold) {
      draggedRef.current = true;
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!draggedRef.current) {
      forwardClickToUnderlay(e.clientX, e.clientY);
      suppressNextClickOnce();
    }
    startRef.current = null;
    draggedRef.current = false;
  }

  function suppressNextClickOnce() {
    const startedAt = Date.now();
    lastForwardAtRef.current = startedAt;

    const onWinClickCapture = (ev: MouseEvent) => {
      // 합성 직후 120ms 내 들어오는 자연 클릭을 컷
      if (Date.now() - startedAt < 120) {
        ev.stopImmediatePropagation?.();
        ev.stopPropagation();
        ev.preventDefault();
      }
      window.removeEventListener('click', onWinClickCapture, true);
    };

    window.addEventListener('click', onWinClickCapture, true);
  }

  return (
    <div
      data-component="BottomSheetDragHandle"
      ref={dragHandleRef}
      className={`fixed left-0 right-0 top-0`}
      style={{ height: `${height}px`, zIndex: 3, touchAction: 'none', pointerEvents: 'auto', top: `${offsetY}px` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClickCapture={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      aria-hidden="true"
    >
      <div className="pointer-events-auto mx-auto h-full w-full cursor-grab active:cursor-grabbing" aria-label="Drag handle" role="button" style={{ touchAction: 'none' }} />
    </div>
  );
};
